import { motion } from "framer-motion";
import { Layers, Sparkles, Zap, Gift, Gem } from "lucide-react";
import { useAppLaunch } from "@/components/landing/AppLaunch";
import { useLang } from "@/i18n/LanguageContext";

const icons = [Layers, Zap, Sparkles];

export const WhyJosfit = () => {
  const { launchApp } = useAppLaunch();
  const { t } = useLang();
  return (
    <section data-testid="why-section" className="relative py-24 sm:py-32">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="text-center max-w-2xl mx-auto">
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-400">
            {t.why.overline}
          </span>
          <h2 className="mt-4 font-outfit text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-white">
            {t.why.title}
          </h2>
        </div>

        <div className="mt-14 grid md:grid-cols-3 gap-6">
          {t.why.reasons.map((r, i) => {
            const Icon = icons[i] || Layers;
            return (
              <motion.div
                key={i}
                data-testid={`why-card-${i}`}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                className="rounded-2xl border border-white/10 bg-white/[0.03] p-8"
              >
                <span className="grid place-items-center h-12 w-12 rounded-xl bg-emerald-500/10 text-emerald-400 ring-1 ring-emerald-400/20">
                  <Icon className="h-6 w-6" />
                </span>
                <h3 className="mt-5 font-outfit text-xl font-bold text-white">{r.title}</h3>
                <p className="mt-2 text-slate-400 leading-relaxed">{r.desc}</p>
              </motion.div>
            );
          })}
        </div>

        <motion.div
          data-testid="pricing-banner"
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="mt-8 overflow-hidden rounded-3xl border border-emerald-400/20 bg-gradient-to-br from-emerald-500/[0.08] to-transparent p-8 sm:p-10"
        >
          <div className="flex flex-col lg:flex-row lg:items-center gap-8">
            <div className="flex-1">
              <div className="flex flex-wrap items-center gap-3">
                <span className="inline-flex items-center gap-2 rounded-full bg-emerald-400/15 px-4 py-1.5 text-sm font-bold text-emerald-300">
                  <Gift className="h-4 w-4" />
                  {t.why.free}
                </span>
                <span className="inline-flex items-center gap-2 rounded-full bg-violet-400/15 px-4 py-1.5 text-sm font-bold text-violet-300">
                  <Gem className="h-4 w-4" />
                  {t.why.premium}
                </span>
              </div>
              <p className="mt-4 text-lg text-slate-300 max-w-2xl">{t.why.pricingText}</p>
            </div>
            <button
              type="button"
              onClick={launchApp}
              data-testid="why-cta"
              className="shrink-0 inline-flex items-center justify-center rounded-full bg-emerald-400 px-8 py-4 text-sm font-bold uppercase tracking-wider text-emerald-950 hover:bg-emerald-300 transition-colors duration-300 shadow-lg shadow-emerald-500/25"
            >
              {t.why.cta}
            </button>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
